/**
 * Documentation structure and navigation utilities for Speedcode
 * Defines the hierarchical organization of documentation sections
 */

export interface DocSection {
  id: string;
  title: string;
  description: string;
  path: string;
  icon?: string;
  subsections?: DocSection[];
  order: number;
}

export interface DocCategory {
  id: string;
  title: string;
  description: string;
  sections: DocSection[];
  order: number;
}

/**
 * Main documentation structure
 * Organized by categories and sections for easy navigation and extensibility
 */
export const DOCUMENTATION_STRUCTURE: DocCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Learn the basics of using Speedcode',
    order: 1,
    sections: [
      {
        id: 'overview',
        title: 'Platform Overview',
        description: 'Introduction to Speedcode and its goals',
        path: '/docs/overview',
        icon: 'info',
        order: 1,
      },
      {
        id: 'first-problem',
        title: 'Solving Your First Problem',
        description: 'Step-by-step guide to solving a speedcode problem',
        path: '/docs/first-problem',
        icon: 'play',
        order: 2,
      },
      {
        id: 'understanding-results',
        title: 'Understanding Results',
        description: 'How to interpret submission results and performance metrics',
        path: '/docs/understanding-results',
        icon: 'chart',
        order: 3,
      },
    ],
  },
  {
    id: 'problem-solving',
    title: 'Problem Solving',
    description: 'Advanced techniques and best practices',
    order: 2,
    sections: [
      {
        id: 'optimization-strategies',
        title: 'Optimization Strategies',
        description: 'Common techniques for improving performance',
        path: '/docs/optimization-strategies',
        icon: 'zap',
        order: 1,
      },
      {
        id: 'parallel-programming',
        title: 'Parallel Programming',
        description: 'Guide to using OpenCilk for parallel optimization',
        path: '/docs/parallel-programming',
        icon: 'cpu',
        order: 2,
      },
      {
        id: 'debugging-tips',
        title: 'Debugging & Troubleshooting',
        description: 'Common issues and how to resolve them',
        path: '/docs/debugging-tips',
        icon: 'bug',
        order: 3,
      },
    ],
  },
  {
    id: 'tools-features',
    title: 'Tools & Features',
    description: 'Advanced platform features and tools',
    order: 3,
    sections: [
      {
        id: 'code-editor',
        title: 'Code Editor Features',
        description: 'Using the built-in code editor effectively',
        path: '/docs/code-editor',
        icon: 'edit',
        order: 1,
      },
      {
        id: 'benchmarking',
        title: 'Performance Benchmarking',
        description: 'Understanding benchmark results and metrics',
        path: '/docs/benchmarking',
        icon: 'timer',
        order: 2,
      },
      {
        id: 'profiling-tools',
        title: 'Profiling Tools',
        description: 'Advanced profiling and analysis features',
        path: '/docs/profiling-tools',
        icon: 'analyzer',
        order: 3,
        subsections: [
          {
            id: 'cilksan',
            title: 'Cilksan Race Detection',
            description: 'Using Cilksan for race condition detection',
            path: '/docs/profiling-tools/cilksan',
            order: 1,
          },
          {
            id: 'cilkscale',
            title: 'Cilkscale Performance Analysis',
            description: 'Scalability analysis with Cilkscale',
            path: '/docs/profiling-tools/cilkscale',
            order: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'contests',
    title: 'Contests',
    description: 'Contest-specific information and guides',
    order: 4,
    sections: [
      {
        id: 'contest-overview',
        title: 'Contest Overview',
        description: 'Understanding Speedcode contests and competitions',
        path: '/docs/contests/overview',
        icon: 'trophy',
        order: 1,
      },
      {
        id: 'contest-submission',
        title: 'Contest Submissions',
        description: 'Special requirements and evaluation for contests',
        path: '/docs/contests/submission',
        icon: 'upload',
        order: 2,
      },
    ],
  },
  {
    id: 'reference',
    title: 'Reference',
    description: 'Technical reference and API documentation',
    order: 5,
    sections: [
      {
        id: 'problem-formats',
        title: 'Problem Formats',
        description: 'Understanding different problem types and formats',
        path: '/docs/reference/problem-formats',
        icon: 'document',
        order: 1,
      },
      {
        id: 'performance-metrics',
        title: 'Performance Metrics',
        description: 'Detailed explanation of all performance measurements',
        path: '/docs/reference/performance-metrics',
        icon: 'graph',
        order: 2,
      },
      {
        id: 'api-reference',
        title: 'API Reference',
        description: 'Technical API documentation for developers',
        path: '/docs/reference/api',
        icon: 'code',
        order: 3,
      },
    ],
  },
];

/**
 * Get all documentation sections in a flat array
 */
export const getAllDocSections = (): DocSection[] => {
  const sections: DocSection[] = [];
  
  DOCUMENTATION_STRUCTURE.forEach(category => {
    category.sections.forEach(section => {
      sections.push(section);
      if (section.subsections) {
        sections.push(...section.subsections);
      }
    });
  });
  
  return sections;
};

/**
 * Find a documentation section by path
 */
export const getDocSectionByPath = (path: string): DocSection | null => {
  const allSections = getAllDocSections();
  return allSections.find(section => section.path === path) || null;
};

/**
 * Get the documentation structure organized by categories
 */
export const getDocumentationStructure = (): DocCategory[] => {
  return DOCUMENTATION_STRUCTURE.sort((a, b) => a.order - b.order);
};

/**
 * Get a specific category by ID
 */
export const getDocCategory = (categoryId: string): DocCategory | null => {
  return DOCUMENTATION_STRUCTURE.find(category => category.id === categoryId) || null;
};

/**
 * Get navigation items for a specific category
 */
export const getCategoryNavigation = (categoryId: string): DocSection[] => {
  const category = getDocCategory(categoryId);
  return category ? category.sections.sort((a, b) => a.order - b.order) : [];
};

/**
 * Get the next and previous sections for navigation
 */
export const getAdjacentSections = (currentPath: string): { prev: DocSection | null; next: DocSection | null } => {
  const allSections = getAllDocSections();
  const currentIndex = allSections.findIndex(section => section.path === currentPath);
  
  return {
    prev: currentIndex > 0 ? (allSections[currentIndex - 1] || null) : null,
    next: currentIndex < allSections.length - 1 ? (allSections[currentIndex + 1] || null) : null,
  };
};

/**
 * Generate breadcrumbs for a documentation path
 */
export const getDocBreadcrumbs = (path: string): { title: string; path: string }[] => {
  const breadcrumbs = [{ title: 'Documentation', path: '/docs' }];
  
  const section = getDocSectionByPath(path);
  if (!section) return breadcrumbs;
  
  // Find the category for this section
  for (const category of DOCUMENTATION_STRUCTURE) {
    const foundSection = category.sections.find(s => 
      s.path === path || (s.subsections && s.subsections.some(sub => sub.path === path))
    );
    
    if (foundSection) {
      breadcrumbs.push({ title: category.title, path: '#' });
      
      if (foundSection.path === path) {
        breadcrumbs.push({ title: foundSection.title, path: foundSection.path });
      } else if (foundSection.subsections) {
        breadcrumbs.push({ title: foundSection.title, path: foundSection.path });
        const subsection = foundSection.subsections.find(sub => sub.path === path);
        if (subsection) {
          breadcrumbs.push({ title: subsection.title, path: subsection.path });
        }
      }
      break;
    }
  }
  
  return breadcrumbs;
};

/**
 * Search documentation sections by title or description
 */
export const searchDocumentation = (query: string): DocSection[] => {
  const allSections = getAllDocSections();
  const lowercaseQuery = query.toLowerCase();
  
  return allSections.filter(section =>
    section.title.toLowerCase().includes(lowercaseQuery) ||
    section.description.toLowerCase().includes(lowercaseQuery)
  );
};

export default {
  DOCUMENTATION_STRUCTURE,
  getAllDocSections,
  getDocSectionByPath,
  getDocumentationStructure,
  getDocCategory,
  getCategoryNavigation,
  getAdjacentSections,
  getDocBreadcrumbs,
  searchDocumentation,
}; 